<div class="mt-4 mx-4">

  <app-breadcrumb [items]="breadcrumbItems"></app-breadcrumb>

  <!-- Page Header -->
  <div class="page-header-bar card shadow-sm border-0 mb-3">
    <div class="card-body d-flex align-items-center justify-content-between flex-wrap gap-2 py-3">
      <div class="d-flex align-items-center gap-3">
        <div class="header-icon-wrap">
          <i class="bi bi-sliders2-vertical"></i>
        </div>
        <div>
          <h5 class="mb-0 fw-bold page-title">Late Fee Configuration</h5>
          <small class="text-muted">Manage versioned late fee settings per employer</small>
        </div>
      </div>
      <button
        *ngIf="authService.hasAccess('configuration.lateFeeConfiguration', 'Add')"
        class="btn add-config-btn d-flex align-items-center gap-2"
        (click)="openAddForm()">
        <i class="bi bi-plus-lg"></i>
        Add Configuration
      </button>
    </div>
  </div>

  <!-- Table Card -->
  <div class="card shadow-sm border-0">
    <div class="card-body">

      <!-- Search + Filter bar -->
      <div class="mb-3">
        <div class="d-flex align-items-center gap-2 flex-nowrap">
          <div class="position-relative flex-grow-1 min-width-0">
            <input id="searchUser" type="text" class="form-control lfc-search-input shadow-none"
              placeholder="Type here..." [(ngModel)]="searchText" (input)="performSearch()" />
            <span class="search-icon" (click)="performSearch()">
              <i class="bi bi-search"></i>
            </span>
          </div>
          <div class="flex-shrink-0 advanced-filter-wrapper">
            <app-filter-panel
              title="Advanced Filter"
              [fields]="filterFields"
              (filterApplied)="onFilter($event)"
              (filterCleared)="onClearFilter()">
            </app-filter-panel>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div *ngIf="!isLoading && filteredSections.length === 0" class="text-center py-5">
        <i class="bi bi-inbox fs-1 text-muted mb-3"></i>
        <p class="text-muted mb-0">No late fee configurations found.</p>
        <small class="text-muted">Use the filter to search or add a new configuration.</small>
      </div>

      <!-- Single unified table -->
      <div *ngIf="!isLoading && filteredSections.length > 0" class="lfc-table-card">
        <div class="table-responsive">
          <table class="lfc-table">
            <colgroup>
              <col class="lfc-col-type">
              <col class="lfc-col-fee">
              <col class="lfc-col-kind">
              <col class="lfc-col-ver">
              <col class="lfc-col-status">
              <col class="lfc-col-date">
              <col class="lfc-col-act">
            </colgroup>

            <!-- Single global header -->
            <thead>
              <tr>
                <th>Fee Type</th>
                <th class="text-end">Late Fee</th>
                <th class="text-center">Kind</th>
                <th class="text-center">Version</th>
                <th class="text-center">Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              <ng-container *ngFor="let section of filteredSections">

                <!-- Company group-header row -->
                <tr class="lfc-row-company">
                  <td colspan="7">
                    <div class="lfc-company-cell">
                      <i class="bi bi-building lfc-company-icon"></i>
                      <div class="lfc-company-meta">
                        <span class="lfc-company-id">{{ section.companyName ?? section.companyId }}</span>
                        <span class="lfc-company-name">ID: {{ section.companyId }}</span>
                      </div>
                      <span class="lfc-company-count">
                        {{ getTotalConfigCount(section) }} configuration{{ getTotalConfigCount(section) !== 1 ? 's' : '' }}
                      </span>
                    </div>
                  </td>
                </tr>

                <!-- Fee type rows -->
                <ng-container *ngFor="let group of section.groups">

                  <!-- Current / primary row -->
                  <tr class="lfc-row-fee">
                    <td>
                      <div class="lfc-type-cell">
                        <button *ngIf="group.historical.length > 0"
                          class="lfc-expand-btn"
                          [class.open]="isGroupExpanded(getGroupKey(group))"
                          (click)="toggleGroup(getGroupKey(group))"
                          title="Show history">
                          <i class="bi bi-chevron-right"></i>
                        </button>
                        <span *ngIf="group.historical.length === 0" class="lfc-expand-spacer"></span>
                        <span class="lfc-type-label">{{ group.primary.typeCode ?? getTypeLabelByTypeId(group.primary.lateFeeTypeId) }}</span>
                        <span *ngIf="group.historical.length > 0 && !isGroupExpanded(getGroupKey(group))" class="lfc-hist-count-badge">
                          +{{ group.historical.length }} history
                        </span>
                      </div>
                    </td>
                    <td class="text-end fw-semibold">
                      {{ group.primary.lateFee | number:'1.2-2' }}
                      <span class="fee-unit-text">{{ group.primary.isPercentage ? '%' : 'LKR' }}</span>
                    </td>
                    <td class="text-center">
                      <span class="badge" [ngClass]="group.primary.isPercentage ? 'badge-percentage' : 'badge-fixed'">
                        {{ group.primary.isPercentage ? 'Percentage' : 'Fixed' }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span class="badge" [ngClass]="group.primary.isCurrent ? 'badge-current' : 'badge-historical'">
                        {{ group.primary.isCurrent ? 'Current' : 'Historical' }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span class="badge" [ngClass]="group.primary.isActive ? 'badge-active' : 'badge-inactive'">
                        {{ group.primary.isActive ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="lfc-date-cell">{{ group.primary.createdDateUtc | date:'dd MMM yyyy, HH:mm' }}</td>
                    <td>
                      <div class="d-flex align-items-center justify-content-center gap-1">
                        <button
                          *ngIf="group.primary.isCurrent && group.primary.isActive && authService.hasAccess('configuration.lateFeeConfiguration', 'Edit')"
                          class="btn action-btn-edit p-0" title="Edit"
                          (click)="openEditForm(group.primary)">
                          <i class="bi bi-pencil-square"></i>
                        </button>
                        <button
                          *ngIf="group.primary.isActive && authService.hasAccess('configuration.lateFeeConfiguration', 'Edit')"
                          class="btn action-btn-delete p-0" title="Deactivate"
                          (click)="confirmDelete(group.primary.lateFeeConfigId)">
                          <i class="bi bi-trash3"></i>
                        </button>
                      </div>
                    </td>
                  </tr>

                  <!-- History rows (shown when expanded) -->
                  <ng-container *ngIf="isGroupExpanded(getGroupKey(group))">
                    <tr *ngFor="let hist of group.historical" class="lfc-row-history">
                      <td>
                        <span class="lfc-type-label lfc-type-label-hist">{{ hist.typeCode ?? getTypeLabelByTypeId(hist.lateFeeTypeId) }}</span>
                      </td>
                      <td class="text-end">
                        {{ hist.lateFee | number:'1.2-2' }}
                        <span class="fee-unit-text">{{ hist.isPercentage ? '%' : 'LKR' }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge" [ngClass]="hist.isPercentage ? 'badge-percentage' : 'badge-fixed'" style="opacity:.65">
                          {{ hist.isPercentage ? 'Percentage' : 'Fixed' }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span class="badge badge-historical">Historical</span>
                      </td>
                      <td class="text-center">
                        <span class="badge" [ngClass]="hist.isActive ? 'badge-active' : 'badge-inactive'">
                          {{ hist.isActive ? 'Active' : 'Inactive' }}
                        </span>
                      </td>
                      <td class="lfc-date-cell">{{ hist.createdDateUtc | date:'dd MMM yyyy, HH:mm' }}</td>
                      <td>
                        <div class="d-flex align-items-center justify-content-center gap-1">
                          <button
                            *ngIf="hist.isActive && authService.hasAccess('configuration.lateFeeConfiguration', 'Edit')"
                            class="btn action-btn-delete p-0" title="Deactivate"
                            (click)="confirmDelete(hist.lateFeeConfigId)">
                            <i class="bi bi-trash3"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </ng-container>

                </ng-container>

              </ng-container>
            </tbody>
          </table>
        </div>

        <!-- Paginator -->
        <div class="lfc-table-footer">
          <app-paginator [serverSide]="true" [totalItems]="totalConfigs" [pageSize]="pageSize" [pageSizeOptions]="[5, 10, 20, 50]"
            [activePage]="currentPage" (pageChangeServer)="onPageChange($event)">
          </app-paginator>
        </div>

      </div>

    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════ -->
<!-- Add / Edit Offcanvas                                           -->
<!-- ═══════════════════════════════════════════════════════════════ -->
<div #configOffcanvasEl class="offcanvas offcanvas-end config-offcanvas"
  tabindex="-1" id="configOffcanvas" aria-labelledby="configOffcanvasLabel">

  <div class="offcanvas-header">
    <div class="d-flex align-items-center gap-3">
      <div class="offcanvas-icon-wrap">
        <i class="bi" [ngClass]="mode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'"></i>
      </div>
      <div>
        <h5 class="offcanvas-title mb-0" id="configOffcanvasLabel">
          {{ mode === 'add' ? 'Add Late Fee Configuration' : 'Edit Late Fee Configuration' }}
        </h5>
        <small class="text-muted" *ngIf="mode === 'edit'">
          A new version will be created; the previous record will be deactivated.
        </small>
      </div>
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeOffcanvas()"></button>
  </div>

  <div class="offcanvas-body">
    <form [formGroup]="configForm" (ngSubmit)="onSave()">

      <!-- Version note banner for edit -->
      <div *ngIf="mode === 'edit'" class="version-banner mb-4">
        <i class="bi bi-info-circle-fill me-2"></i>
        Saving will insert a new version and deactivate the current record.
      </div>

      <!-- Employer (Company) -->
      <div class="mb-4">
        <label class="form-label field-label">Employer <span class="text-danger">*</span></label>
        <div class="searchable-dropdown" [class.is-invalid]="configForm.get('companyId')?.invalid && configForm.get('companyId')?.touched">
          <div class="dropdown-input-wrapper">
            <input type="text" class="form-control config-input"
              [value]="getFormSelectedCompanyName()"
              (input)="onFormCompanySearchChange($event)"
              (focus)="openFormCompanyDropdown()"
              (blur)="onFormCompanyBlur()"
              placeholder="Select an employer"
              autocomplete="off"
              [class.is-invalid]="configForm.get('companyId')?.invalid && configForm.get('companyId')?.touched" />
            <span class="input-group-text"><i class="bi bi-chevron-down"></i></span>
          </div>
          <!-- Dropdown Options -->
          <div class="dropdown-list" *ngIf="formCompanyDropdownOpen && formFilteredCompanies().length > 0">
            <div class="dropdown-item" *ngFor="let c of formFilteredCompanies()" (mousedown)="selectFormCompany(c)"
              [class.selected]="formSelectedCompanyId === c.companyId">
              <span class="bank-name">{{ c.companyName }} ({{ c.companyId }})</span>
            </div>
          </div>
          <!-- No Results -->
          <div class="dropdown-list no-results" *ngIf="formCompanyDropdownOpen && formFilteredCompanies().length === 0">
            <div class="dropdown-item">No employers found</div>
          </div>
        </div>
        <div class="invalid-feedback" [style.display]="(configForm.get('companyId')?.invalid && configForm.get('companyId')?.touched) ? 'block' : 'none'">
          Employer is required.
        </div>
      </div>

      <!-- Late Fee Type -->
      <div class="mb-4">
        <label class="form-label field-label">Late Fee Type <span class="text-danger">*</span></label>
        <select
          class="form-select config-input"
          formControlName="lateFeeTypeId"
          [class.is-invalid]="configForm.get('lateFeeTypeId')?.invalid && configForm.get('lateFeeTypeId')?.touched">
          <option [ngValue]="null" disabled selected>Select a fee type</option>
          <option *ngFor="let t of lateFeeTypes" [value]="t.lateFeeTypeId">
            {{ t.typeCode }} — {{ t.description }}
          </option>
        </select>
        <div class="invalid-feedback">Late Fee Type is required.</div>
      </div>

      <!-- Is Percentage toggle -->
      <div class="mb-4">
        <label class="form-label field-label">Fee Calculation Method</label>
        <div class="fee-method-toggle">
          <button
            type="button"
            class="toggle-btn"
            [class.active]="!configForm.get('isPercentage')?.value"
            (click)="configForm.patchValue({ isPercentage: false })">
            <i class="bi bi-cash me-1"></i> Fixed Amount
          </button>
          <button
            type="button"
            class="toggle-btn"
            [class.active]="configForm.get('isPercentage')?.value"
            (click)="configForm.patchValue({ isPercentage: true })">
            <i class="bi bi-percent me-1"></i> Percentage
          </button>
        </div>
      </div>

      <!-- Late Fee Value -->
      <div class="mb-4">
        <label class="form-label field-label">
          Late Fee Value <span class="text-danger">*</span>
          <span class="text-muted ms-1 small">
            ({{ configForm.get('isPercentage')?.value ? '%' : 'LKR' }})
          </span>
        </label>
        <div class="input-group">
          <!-- Fixed Amount: formatted number input with comma separators -->
          <app-formatted-number-input
            *ngIf="!configForm.get('isPercentage')?.value"
            formControlName="lateFee"
            placeholder="0.00"
            [min]="0.01"
            [max]="10000"
            [decimalPlaces]="2"
            [isInvalid]="!!(configForm.get('lateFee')?.invalid && configForm.get('lateFee')?.touched)"
            inputClass="form-control config-input">
          </app-formatted-number-input>
          <!-- Percentage: plain number input -->
          <input class="percentage-input"
            *ngIf="configForm.get('isPercentage')?.value"
            type="number"
            class="form-control config-input"
            formControlName="lateFee"
            placeholder="0.00"
            min="0.01"
            step="0.01"
            max="100"
            [class.is-invalid]="configForm.get('lateFee')?.invalid && configForm.get('lateFee')?.touched" />
          <span class="input-group-text config-input-addon">
            {{ configForm.get('isPercentage')?.value ? '%' : 'LKR' }}
          </span>
        </div>
        <div class="invalid-feedback" [style.display]="(configForm.get('lateFee')?.invalid && configForm.get('lateFee')?.touched) ? 'block' : 'none'">
          <span *ngIf="configForm.get('lateFee')?.errors?.['required']">Late fee value is required.</span>
          <span *ngIf="configForm.get('lateFee')?.errors?.['min']">Must be greater than 0.</span>
          <span *ngIf="configForm.get('lateFee')?.errors?.['max']">Percentage cannot exceed 100%.</span>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="d-flex gap-2 justify-content-end mt-5 pt-3 border-top">
        <button type="button" class="btn btn-cancel" (click)="closeOffcanvas()" [disabled]="isSaving">
          Cancel
        </button>
        <button type="submit" class="btn btn-save d-flex align-items-center gap-2" [disabled]="isSaving || configForm.invalid">
          <span *ngIf="isSaving" class="spinner-border spinner-border-sm"></span>
          <i *ngIf="!isSaving" class="bi bi-check-lg"></i>
          {{ isSaving ? 'Saving...' : 'Save Configuration' }}
        </button>
      </div>

    </form>
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════ -->
<!-- Delete Confirmation Modal                                       -->
<!-- ═══════════════════════════════════════════════════════════════ -->
<div #deleteModalEl class="modal fade" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content delete-modal-content">
      <div class="modal-body text-center p-4">
        <div class="delete-icon-circle mb-3">
          <i class="bi bi-exclamation-triangle-fill text-danger fs-2"></i>
        </div>
        <h5 class="fw-bold mb-2">Deactivate Configuration?</h5>
        <p class="text-muted small mb-4">
          This will soft-delete the selected record by marking it as inactive.
          This action cannot be undone directly.
        </p>
        <div class="d-flex gap-3 justify-content-center">
          <button type="button" class="btn btn-cancel-modal" (click)="cancelDelete()" [disabled]="isDeleting">
            Cancel
          </button>
          <button type="button" class="btn btn-confirm-delete d-flex align-items-center gap-2"
            (click)="executeDelete()" [disabled]="isDeleting">
            <span *ngIf="isDeleting" class="spinner-border spinner-border-sm"></span>
            <i *ngIf="!isDeleting" class="bi bi-trash3"></i>
            {{ isDeleting ? 'Deactivating...' : 'Yes, Deactivate' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
